import axios from "axios"
import qs from "qs"
// constants
import { REACT_APP_STRAPI_API_URL } from "constants/endpoints"
import { languagesTransformer } from "i18n"
import { PAGE_SIZE } from "constants/paginations"
// types
import { Pagination } from "types/api"
import { Language } from "types/i18n"
import { Episode, Speaker, Course } from "types/courses"
import { Nullable } from "types/generics"

export type SliderCourse = Omit<
  Course,
  "intro" | "videoLanguags" | "coursePartner" | "courseDescription" | "superCourse" | "courseLength" | "cmePoints"
> & {
  episodes?: Nullable<
    Omit<Episode, "episodeNumber" | "kalturaVideoId" | "episodeCoverLandscape" | "localizations" | "episodeLength">
  >[]
  speaker?: Nullable<Omit<Speaker, "speakerPicture">>
}

export type GetAllCoursesRequest = {
  locale?: Language
}

export type GetAllCoursesResponse = {
  data: (Omit<Course, "coursePartner" | "courseLength" | "cmePoints" | "courseDescription" | "episodes" | "speaker"> & {
    speaker?: Nullable<Omit<Speaker, "speakerPicture">>
  })[]
  meta: Pagination
}

export type GetCoursesBySuperCoursesRequest = {
  superCoursesIds?: number[]
  specialtiesIds?: number[]
  locale?: Language
  page?: number
  pageSize?: number
}

export type GetCoursesBySuperCoursesResponse = {
  data: SliderCourse[]
  meta: {
    pagination: Pagination
  }
}

export type GetCoursesByCategoriesRequest = {
  specialty: string
  locale?: Language
  page?: number
  pageSize?: number
  searchValue?: string
}

export type GetCoursesByCategoriesResponse = {
  data: SliderCourse[]
  meta: {
    pagination: Pagination
  }
}

export type GetCoursesByCourseTypeResponse = {
  data: SliderCourse[]
}

export type GetInfiniteCoursesBySpecialtyAndTypeResponse = {
  data: SliderCourse[]
  meta: {
    pagination: Pagination
  }
}

export type GetHomePageCoursesResponse = {
  data: SliderCourse[]
}

export type GetCoursesBySpeakerIdRequest = {
  speakerId: number
  locale?: Language
}

export type GetCoursesBySpeakerIdResponse = {
  data: SliderCourse[]
  meta: {
    pagination: Pagination
  }
}

export type GetCommonCoursesRequest = {
  locale?: Language
  filterValue?: string
  page?: number
  pageSize?: number
}

export type GetCommonCoursesResponse = {
  data: SliderCourse[]
  meta: {
    pagination: Pagination
  }
}

export type GetSingleCoursesRequest = {
  locale?: Language
  filterValue?: string
  page?: number
  pageSize?: number
}

export type GetSingleCoursesResponse = {
  data: SliderCourse[]
  meta: {
    pagination: Pagination
  }
}

export type GetNewestCoursesRequest = {
  locale?: Language
  coursesLimit?: number
  withCoursesTypesOnly?: string[]
}

export type GetHomePageCoursesRequest = {
  locale?: Language
  coursesLimit?: number
  courseType?: string
  specialtiesIds?: number[]
  searchValue?: string
}

export type GetInfiniteCoursesBySpecialtyAndTypeRequest = {
  locale?: Language
  coursesLimit?: number
  courseType?: string
  specialtiesIds?: number[]
  searchValue?: string
  page?: number
  pageSize?: number
}

export type GetNewestCoursesResponse = {
  data: SliderCourse[]
  meta: Pagination
}

export type GetMostWatchedCoursesRequest = {
  locale?: Language
  withCoursesTypesOnly?: string[]
  specialtiesIds?: number[]
}

export type GetHighlightedCoursesRequest = {
  locale?: Language
}

export type GetMostWatchedCoursesResponse = {
  data: SliderCourse[]
  meta: Pagination
}

export type GetHightlightedCoursesResponse = {
  data: SliderCourse[]
}

export const getAllCourses = async ({ locale = "en" }: GetAllCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      sort: { id: "DESC" },
      locale: languagesTransformer[_locale],

      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePointsText",
        "courseName",
        "certified",
        "hashCourseCover",
        "hashCourseCoverLandscape",
        "locale",
      ],
      populate: {
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        superCourse: { fields: ["id"] },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/courses?${params}`

  const response = await axios.get<GetAllCoursesResponse>(url)

  return response.data
}

export const getCoursesBySuperCourses = async ({
  locale = "en",
  superCoursesIds,
  specialtiesIds,
  page = 1,
  pageSize = PAGE_SIZE,
}: GetCoursesBySuperCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      sort: { id: "DESC" },
      locale: languagesTransformer[_locale],

      filters: {
        superCourse: {
          id: {
            $in: superCoursesIds,
          },
        },
        specialties: {
          id: {
            $in: specialtiesIds,
          },
        },
      },
      pagination: {
        page,
        pageSize,
      },
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "certified",
        "hashCourseCover",
        "hashCourseCoverLandscape",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        cover: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}courses?${params}`

  const response = await axios.get<GetCoursesBySuperCoursesResponse>(url)

  return response.data
}

export const getCoursesByCategories = async ({
  specialty,
  locale = "en",
  page = 1,
  pageSize = PAGE_SIZE,
  searchValue,
}: GetCoursesByCategoriesRequest) => {
  const _locale = locale.slice(0, 2) as Language
  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],
      filters: {
        specialties: {
          name: {
            $eq: specialty,
          },
        },
        ...(searchValue
          ? {
              $or: [
                {
                  courseName: {
                    $containsi: searchValue,
                  },
                },
                {
                  courseDescription: {
                    $containsi: searchValue,
                  },
                },
                {
                  courseKeywords: {
                    $containsi: searchValue,
                  },
                },
                {
                  specialties: {
                    name: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  episodes: {
                    episodeName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerFirstName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerLastName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerTitle: {
                      $containsi: searchValue,
                    },
                  },
                },
              ],
            }
          : {}),
      },
      pagination: {
        page,
        pageSize,
      },
      sort: { publishedAt: "DESC" },
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "certified",
        "hashCourseCover",
        "hashCourseCoverLandscape",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        cover: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}courses?${params}`

  const response = await axios.get<GetCoursesByCategoriesResponse>(url)

  return response.data
}

export const getCoursesBySpeakerId = async ({ speakerId, locale = "en" }: GetCoursesBySpeakerIdRequest) => {
  const _locale = locale.slice(0, 2) as Language
  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],
      filters: {
        speaker: {
          id: {
            $eq: speakerId,
          },
        },
      },
      sort: { publishedAt: "DESC" },
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLength",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "certified",
        "hashCourseCover",
        "hashCourseCoverLandscape",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}courses?${params}`

  const response = await axios.get<GetCoursesBySpeakerIdResponse>(url)

  return response.data
}

export const getCommonCourses = async ({
  locale = "en",
  filterValue,
  page = 1,
  pageSize = PAGE_SIZE,
}: GetCommonCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language
  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],
      pagination: {
        page,
        pageSize,
      },
      filters: {
        $and: [
          {
            courseType: {
              $ne: "shortUnit",
            },
          },
          {
            courseType: {
              $ne: "journal",
            },
          },
        ],
        ...(filterValue
          ? {
              $or: [
                {
                  courseName: {
                    $containsi: filterValue,
                  },
                },
                {
                  courseDescription: {
                    $containsi: filterValue,
                  },
                },
                {
                  courseKeywords: {
                    $containsi: filterValue,
                  },
                },
                {
                  specialties: {
                    name: {
                      $containsi: filterValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerFirstName: {
                      $containsi: filterValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerLastName: {
                      $containsi: filterValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerTitle: {
                      $containsi: filterValue,
                    },
                  },
                },
              ],
            }
          : {}),
      },
      sort: { publishedAt: "DESC" },
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "certified",
        "hashCourseCover",
        "hashCourseCoverLandscape",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        cover: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}courses?${params}`

  const response = await axios.get<GetCommonCoursesResponse>(url)

  return response.data
}

export const getSingleCourses = async ({
  locale = "en",
  filterValue,
  page = 1,
  pageSize = PAGE_SIZE,
}: GetSingleCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language
  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],
      pagination: {
        page,
        pageSize,
      },
      filters: {
        courseType: {
          $eq: ["shortUnit", "journal"],
        },
        episodes: {
          id: {
            $notNull: true,
          },
        },
        ...(filterValue
          ? {
              $or: [
                {
                  courseKeywords: {
                    $containsi: filterValue,
                  },
                },
                {
                  courseName: {
                    $containsi: filterValue,
                  },
                },
                {
                  courseDescription: {
                    $containsi: filterValue,
                  },
                },
                {
                  specialties: {
                    name: {
                      $containsi: filterValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerFirstName: {
                      $containsi: filterValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerLastName: {
                      $containsi: filterValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerTitle: {
                      $containsi: filterValue,
                    },
                  },
                },
              ],
            }
          : {}),
      },
      sort: { publishedAt: "DESC" },
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLength",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "certified",
        "hashCourseCover",
        "hashCourseCoverLandscape",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        cover: {
          fields: ["url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}courses?${params}`

  const response = await axios.get<GetSingleCoursesResponse>(url)

  return response.data
}

export const getNewestCourses = async ({
  locale = "en",
  coursesLimit = 10,
  withCoursesTypesOnly,
}: GetNewestCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      sort: ["publishedAt:DESC"],
      locale: languagesTransformer[_locale],
      pagination: { limit: coursesLimit },
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "hashCourseCover",
        "hashCourseCoverLandscape",
        "locale",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
      },
      filters: {
        ...(withCoursesTypesOnly && {
          courseType: {
            $eq: withCoursesTypesOnly,
          },
        }),
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/courses?${params}`

  const response = await axios.get<GetNewestCoursesResponse>(url)

  return response.data
}

export const getMostWatchedCourses = async ({
  locale = "en",
  withCoursesTypesOnly,
  specialtiesIds,
}: GetMostWatchedCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],

      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "hashCourseCover",
        "hashCourseCoverLandscape",
        "locale",
        "subType",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
        superCourse: { fields: ["id", "watchedCount"] },
      },
      filters: {
        ...(withCoursesTypesOnly && {
          courseType: {
            $eq: withCoursesTypesOnly,
          },
        }),
        ...(specialtiesIds && {
          specialties: {
            id: {
              $in: specialtiesIds,
            },
          },
        }),
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/top-monthly-courses?${params}`

  const response = await axios.get<GetMostWatchedCoursesResponse>(url)

  return response.data
}

export const getHighlightedCourses = async ({ locale = "en" }: GetHighlightedCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      locale: languagesTransformer[_locale],
      sort: ["sortOrder:ASC", "createdAt:DESC"],
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "hashCourseCover",
        "hashCourseCoverLandscape",
        "locale",
        "highlight",
        "subType",
        "sortOrder",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
        superCourse: { fields: ["id", "watchedCount"] },
      },
      filters: {
        highlight: true,
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/courses?${params}`

  const response = await axios.get<GetHightlightedCoursesResponse>(url)

  return response.data
}

export const getHomePageCourses = async ({
  locale = "en",
  courseType,
  specialtiesIds,
  searchValue,
}: GetHomePageCoursesRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      sort: ["publishedAt:DESC"],
      locale: languagesTransformer[_locale],
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "hashCourseCover",
        "hashCourseCoverLandscape",
        "locale",
        "subType",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
        relatedSpecialties: {
          fields: ["name"],
        },
      },
      filters: {
        ...(courseType && {
          courseType: {
            $eq: courseType === "mainCourses" ? ["basicCourse", "expertCourse", "videoCasts"] : courseType,
          },
        }),
        ...(specialtiesIds && specialtiesIds.length > 0
          ? {
              $or: [
                {
                  specialties: {
                    id: {
                      $in: specialtiesIds,
                    },
                  },
                },
                {
                  relatedSpecialties: {
                    id: {
                      $in: specialtiesIds,
                    },
                  },
                },
              ],
            }
          : {}),
        ...(searchValue
          ? {
              $or: [
                {
                  courseName: {
                    $containsi: searchValue,
                  },
                },
                {
                  courseDescription: {
                    $containsi: searchValue,
                  },
                },
                {
                  courseKeywords: {
                    $containsi: searchValue,
                  },
                },
                {
                  specialties: {
                    name: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  relatedSpecialties: {
                    name: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  episodes: {
                    episodeName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerFirstName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerLastName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerTitle: {
                      $containsi: searchValue,
                    },
                  },
                },
              ],
            }
          : {}),
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/courses?${params}`

  const response = await axios.get<GetHomePageCoursesResponse>(url)

  return response.data
}

export const getInfiniteCoursesBySpecialtyAndType = async ({
  locale = "en",
  courseType,
  specialtiesIds,
  searchValue,
  page = 1,
  pageSize = PAGE_SIZE,
}: GetInfiniteCoursesBySpecialtyAndTypeRequest) => {
  const _locale = locale.slice(0, 2) as Language

  const params = qs.stringify(
    {
      sort: ["publishedAt:DESC"],
      locale: languagesTransformer[_locale],
      fields: [
        "publishedAt",
        "firstPublishedAt",
        "courseType",
        "courseLengthText",
        "cmePoints",
        "cmePointsText",
        "courseName",
        "hashCourseCover",
        "hashCourseCoverLandscape",
        "locale",
      ],
      populate: {
        episodes: {
          fields: ["episodeLength", "episodeLengthText", "episodeName", "publishedAt", "hashEpisodeCover"],
          populate: {
            episodeCover: {
              fields: ["ext", "url"],
            },
          },
        },
        cover: {
          fields: ["ext", "url"],
        },
        coverLandscape: {
          fields: ["ext", "url"],
        },
        speaker: {
          fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
        },
        specialties: {
          fields: ["name"],
        },
      },
      filters: {
        ...(courseType && {
          courseType: {
            $eq: courseType === "mainCourses" ? ["basicCourse", "expertCourse", "videoCasts"] : courseType,
          },
        }),
        specialties: {
          id: {
            $in: specialtiesIds,
          },
        },
        ...(searchValue
          ? {
              $or: [
                {
                  courseName: {
                    $containsi: searchValue,
                  },
                },
                {
                  courseDescription: {
                    $containsi: searchValue,
                  },
                },
                {
                  courseKeywords: {
                    $containsi: searchValue,
                  },
                },
                {
                  specialties: {
                    name: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  episodes: {
                    episodeName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerFirstName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerLastName: {
                      $containsi: searchValue,
                    },
                  },
                },
                {
                  speaker: {
                    speakerTitle: {
                      $containsi: searchValue,
                    },
                  },
                },
              ],
            }
          : {}),
      },
      pagination: {
        page,
        pageSize,
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `/courses?${params}`

  const response = await axios.get<GetInfiniteCoursesBySpecialtyAndTypeResponse>(url)

  return response.data
}
